import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, Check, Info, Tag } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow, PageHero, CtaBand } from "../components/Shared";
import { BUSINESS, PACKAGES, UPGRADES, PAINT_CORRECTION, CERAMIC, photo, fmt } from "../data/site";

export default function Pricing() {
  return (
    <>
      <Seo
        title="Detailing Prices & Packages | A+ Detailing Sioux Falls, SD"
        description="Compare A+ Detailing packages: Interior Detail from $275, Full Auto Detailing from $325, The Exclusive from $450. Upgrades, 2-step paint correction from $400 and System X ceramic coatings."
        path="/pricing"
      />
      <PageHero
        eyebrow="Pricing & Packages"
        lines={["HONEST", "PRICING."]}
        lead="Flat rates for every package — in-shop or mobile at your location. Pricing varies only by the size of your vehicle, never its condition."

        chips={["Mobile & In-Shop", "Free quotes for PPF & tint"]}
        testPrefix="pricing"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <Reveal><Eyebrow>Detailing Packages</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <div data-testid="pricing-flat-rate-note" className="mt-6 flex items-start gap-3 rounded-xl border border-bright/30 bg-bright/10 px-5 py-4 max-w-3xl">
            <Tag size={16} className="text-bright shrink-0 mt-0.5" />
            <p className="text-sm text-white leading-relaxed">
              <strong>Every detailing package is a flat rate.</strong> Pricing varies by the size of your vehicle — never its condition.
            </p>
          </div>
        </Reveal>
        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div
                data-testid={`pricing-package-${i}`}
                className={`h-full rounded-2xl border p-8 ${p.featured ? "border-bright/50 bg-gradient-to-b from-bright/10 to-card blue-glow" : "border-white/10 bg-card"}`}
              >
                <h3 className="font-display text-2xl font-bold">{p.name}</h3>
                <div className="mt-6 space-y-3">
                  <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
                    <span className="text-sm text-steel">In-Shop, starting</span>
                    <span className="font-display text-4xl font-extrabold">{fmt(p.price)}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-steel">Mobile, starting</span>
                    <span className="font-display text-2xl font-bold text-bright">{fmt(p.mobilePrice)}</span>
                  </div>
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.includes.map((inc, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-steel">
                      <Check size={14} className="text-bright mt-0.5 shrink-0" /> {inc}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/book"
                  data-testid={`pricing-book-${i}`}
                  className={`mt-8 flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${p.featured ? "bg-bright hover:bg-accent text-white" : "border border-white/20 hover:border-bright hover:text-bright"}`}
                >
                  Book This Package <ArrowUpRight size={15} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-coal border-y border-white/5 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <Reveal><Eyebrow>Interior Upgrades</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Add-ons for any detail.</h2>
            </Reveal>
            <div className="mt-9 rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="pricing-upgrades-table">
              {UPGRADES.map((u, i) => (
                <div key={i} className="flex items-center justify-between gap-6 px-6 py-5 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                  <span className="text-sm text-steel">{u.name}</span>
                  <span className="font-mono text-sm text-bright shrink-0">+{fmt(u.price)}</span>
                </div>
              ))}
            </div>
            <Reveal delay={0.15}>
              <p className="mt-5 flex items-start gap-2 text-xs text-muted">
                <Info size={14} className="text-bright shrink-0 mt-0.5" />
                Upgrades can be added to any detailing package — mention them when you book.
              </p>
            </Reveal>
          </div>

          <div className="space-y-5">
            <Reveal>
              <div className="rounded-2xl border border-white/10 bg-card p-8" data-testid="pricing-paint-correction">
                <Eyebrow>Paint Correction</Eyebrow>
                <div className="mt-3 flex items-end gap-3">
                  <span className="font-display text-4xl font-extrabold">{fmt(PAINT_CORRECTION.price)}</span>
                  <span className="text-muted text-sm mb-1">starting</span>
                </div>
                <p className="mt-4 text-sm text-steel leading-relaxed">{PAINT_CORRECTION.blurb}</p>
                <Link to="/paint-correction" className="mt-5 inline-flex items-center gap-1.5 text-sm text-bright hover:text-white transition-colors">
                  About 2-step correction <ArrowUpRight size={14} />
                </Link>
              </div>
            </Reveal>
            {CERAMIC.map((c, i) => (
              <Reveal key={c.name} delay={0.08 + i * 0.08}>
                <div className={`rounded-2xl border p-8 ${c.featured ? "border-bright/50 bg-gradient-to-b from-bright/10 to-card" : "border-white/10 bg-card"}`} data-testid={`pricing-ceramic-${i}`}>
                  <Eyebrow>Ceramic Coating</Eyebrow>
                  <div className="mt-3 flex items-end justify-between">
                    <h3 className="font-display text-2xl font-bold">{c.name}</h3>
                    <span className="font-display text-3xl font-extrabold">{fmt(c.price)}</span>
                  </div>
                  <p className="mt-3 text-sm text-steel">{c.warrantyLine}</p>
                  <Link to="/ceramic-coatings" className="mt-4 inline-flex items-center gap-1.5 text-sm text-bright hover:text-white transition-colors">
                    Coating details <ArrowUpRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <p className="text-xs text-muted px-1" data-testid="pricing-ceramic-note">
                Every ceramic booking includes a free paint correction and Carfax registration.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="rounded-2xl border border-white/10 bg-card p-8" data-testid="pricing-quote-services">
                <Eyebrow>Quoted Per Vehicle</Eyebrow>
                <div className="mt-3 flex items-center justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-bold">PPF & Window Tinting</h3>
                    <p className="mt-1.5 text-sm text-steel">Priced by vehicle — call or request a free quote.</p>
                  </div>
                  <a href={BUSINESS.phoneHref} data-testid="pricing-quote-call-button" className="shrink-0 flex items-center gap-2 bg-bright hover:bg-accent text-white text-sm font-semibold rounded-full px-5 py-2.5 transition-colors">
                    <Phone size={14} /> Call
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand testPrefix="pricing" />
    </>
  );
}
